import React, { useState, useEffect, useRef } from 'react'
import { Collapse } from '@mui/material'
import { SelectField, DatePicker } from '../../Base/Form/index.js'
import * as lib from '../../../../js/lib/lib.js'

import SelectLocation from '../../BookingWidget/SelectLocation.js'
import generateTimeOptions from '../../../../js/helpers/generateTimeOptions.js'

const now = new Date()
const oneYearLater = new Date()
const OneYearAndMonthLater = new Date()
oneYearLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setMonth(OneYearAndMonthLater.getMonth() + 1)
const timeOptions = generateTimeOptions()

const ReservationFields = ({
  control,
  errors,
  setValue,
  watch,
  disabled = false,
  currentValues,
  pendingPayment = false,
}) => {
  const { dictionary, configuration } = window.modifyData
  const [toggleCollapse, setToggleCollapse] = useState(false)
  const [isSameDay, setIsSameDay] = useState(false)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)
  const dropOffDateRef = useRef(null)
  const dropOffTimeRef = useRef(null)

  useEffect(() => {
    if (watch('pickUpDate') && watch('dropOffDate')) {
      const pickUp = new Date(watch('pickUpDate')).getTime()
      const dropOff = new Date(watch('dropOffDate')).getTime()
      if (pickUp > dropOff) {
        setValue('dropOffDate', '')
      } else if (pickUp === dropOff) {
        setIsSameDay(true)
      } else {
        setIsSameDay(false)
      }
    }
  }, [watch('pickUpDate'), watch('dropOffDate')])

  const handleCollapseClick = () => {
    if (lib.scrDevices()) {
      setToggleCollapse((old) => !old)
    }
  }

  useEffect(() => {
    const handleResize = () => {
      if (!lib.scrDevices()) {
        setToggleCollapse(true)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (watch('pickUpDate') && !watch('pickUpTime')) {
      setValue('pickUpTime', '10:00', { shouldValidate: true })
    }
  }, [watch('pickUpDate'), watch('pickUpTime')])

  useEffect(() => {
    if (watch('dropOffDate')) {
      const currentDropOffTime = watch('dropOffTime')
      
      // Check if same day and pickup time is set
      if (isSameDay && watch('pickUpTime')) {
        const pickUpTimeIndex = timeOptions.indexOf(watch('pickUpTime'))
        const availableTimes = timeOptions.filter(
          (_, index) => index > pickUpTimeIndex
        )
        
        // Check if current dropoff time is invalid (not in available times or not set)
        const isInvalidTime = !currentDropOffTime || !availableTimes.includes(currentDropOffTime)
        
        if (isInvalidTime && availableTimes.length > 0) {
          // Use first available time after pickup
          setValue('dropOffTime', availableTimes[0], { shouldValidate: true })
        }
      } else if (!currentDropOffTime) {
        // Different days and no time set - use default
        setValue('dropOffTime', '10:00', { shouldValidate: true })
      }
    }
  }, [watch('dropOffDate'), watch('dropOffTime'), isSameDay, watch('pickUpTime')])

  const handlePickUpDateChange = (selectedDate) => {
    if (!selectedDate) {
      pickUpTimeRef.current?.open()
      return
    }

    const nextDay = new Date(selectedDate)
    nextDay.setDate(nextDay.getDate() + 1)
    const currentDropOffDate = watch('dropOffDate')

    // Only update drop-off date if it's not set or is earlier than next day
    const shouldUpdateDropOff =
      !currentDropOffDate ||
      new Date(currentDropOffDate).getTime() < nextDay.getTime()

    if (shouldUpdateDropOff) {
      setValue('dropOffDate', nextDay.toISOString().split('T')[0], {
        shouldValidate: true,
      })
    }

    pickUpTimeRef.current?.open()
  }

  return (
    <>
      <div className="s-booking-form__form-group">
        <div className="row" onClick={handleCollapseClick}>
          <div className="col-lg-6 mb-24 d-flex align-items-center justify-content-between">
            <span className="s-booking-form__form-group__title">
              {dictionary.reservationDetailsTitle}
            </span>
            <div className="d-lg-none ">
              <i
                className={`icon-arrow-down arrow ${
                  toggleCollapse ? 'open' : ''
                }`}
              />
            </div>
          </div>
          <div className="col-lg-6 d-flex justify-content-lg-end align-items-end mb-24">
            <span className="s-booking-form__form-group__required">
              {dictionary.reservationDetailsRequiredLabel}
            </span>
          </div>
        </div>

        <Collapse in={toggleCollapse}>
          <div className="row">
            <div className="col-lg-6 mb-24">
              <SelectLocation
                name={'pickUpLocation'}
                label={dictionary.pickUpLabel}
                control={control}
                setValue={setValue}
                watch={watch}
                error={errors.pickUpLocation}
                disabled={disabled || pendingPayment}
                locationSearchApi={configuration.locationSearch}
                locationFindApi={configuration.locationFind}
                dictionary={dictionary}
              />
            </div>
            <div className="col-lg-6 mb-24">
              <SelectLocation
                name={'dropOffLocation'}
                label={dictionary.dropOffLabel}
                control={control}
                setValue={setValue}
                watch={watch}
                error={errors.dropOffLocation}
                disabled={disabled || pendingPayment}
                locationSearchApi={configuration.locationSearch}
                locationFindApi={configuration.locationFind}
                dictionary={dictionary}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6 mb-24">
              <div className="s-booking-widget__date-time">
                <DatePicker
                  ref={pickUpDateRef}
                  name="pickUpDate"
                  label={dictionary.pickUpDateLabel}
                  control={control}
                  setValue={setValue}
                  disableBefore={new Date()}
                  disableAfter={oneYearLater}
                  error={errors.pickUpDate}
                  disabled={disabled || pendingPayment}
                  dictionary={dictionary}
                  defaultValue={currentValues?.pickUpDate}
                  onChange={handlePickUpDateChange}
                />

                <SelectField
                  ref={pickUpTimeRef}
                  name={'pickUpTime'}
                  control={control}
                  label={dictionary.timeLabel}
                  options={timeOptions.map((option) => ({
                    value: option,
                    text: option,
                  }))}
                  error={errors.pickUpTime}
                  disabled={disabled || pendingPayment}
                  isSelectTime
                  onChange={() => dropOffDateRef.current?.open()}
                />
              </div>
            </div>
            <div className="col-lg-6 mb-24">
              <div className="s-booking-widget__date-time">
                <DatePicker
                  ref={dropOffDateRef}
                  name="dropOffDate"
                  label={dictionary.dropOffDateLabel}
                  control={control}
                  setValue={setValue}
                  disableBefore={
                    watch('pickUpDate')
                      ? new Date(watch('pickUpDate'))
                      : new Date()
                  }
                  disableAfter={OneYearAndMonthLater}
                  error={errors.dropOffDate}
                  disabled={disabled || pendingPayment}
                  dictionary={dictionary}
                  defaultValue={currentValues?.dropOffDate}
                  onChange={() => dropOffTimeRef.current?.open()}
                />
                <SelectField
                  ref={dropOffTimeRef}
                  name={'dropOffTime'}
                  control={control}
                  label={dictionary.timeLabel}
                  options={
                    watch('pickUpTime') && isSameDay
                      ? timeOptions
                          ?.filter(
                            (_, index) =>
                              index > timeOptions.indexOf(watch('pickUpTime'))
                          )
                          ?.map((option) => ({
                            value: option,
                            text: option,
                          }))
                      : timeOptions?.map((option) => ({
                          value: option,
                          text: option,
                        }))
                  }
                  error={errors.dropOffTime}
                  disabled={disabled || pendingPayment}
                />
              </div>
            </div>
          </div>
        </Collapse>
      </div>
    </>
  )
}

export default ReservationFields
